/**
 * Entitlement API hooks for managing entitlements
 * Provides React Query hooks for fetching entitlement catalog
 */

import { useQuery } from '@tanstack/react-query';
import { authenticatedApi } from '../lib/authenticated-api';

// Entitlement definition type (matches backend)
export interface EntitlementDefinition {
  id: string;
  name: string;
  description: string;
  stripeProductId?: string;
}

export interface SubscriptionBundle {
  stripeProductId: string;
  name: string;
  entitlements: string[];
}

/**
 * A non-Stripe provider's catalog entry, as this ENVIRONMENT sees it:
 * `providerProductIds` arrives already merged with the environment's own
 * configured ids, so it is exactly the set the grant path matches against here.
 *
 * Unlike `SubscriptionBundle` this mapping is EXHAUSTIVE for its provider —
 * `getExternalBundle` is the only route to a grant — which is what makes
 * "this product is in no bundle, so it grants nothing" a sound inference for
 * these providers and an unsound one for Stripe.
 */
export interface ExternalProviderBundle {
  provider: string;
  providerProductIds: string[];
  bundleId: string;
  entitlements: string[];
  label: string;
}

export interface EntitlementListResponse {
  entitlements: EntitlementDefinition[];
  bundles: SubscriptionBundle[];
  externalBundles?: ExternalProviderBundle[];
}

// Query keys
const entitlementQueryKeys = {
  all: ['admin', 'entitlements'] as const,
};

/**
 * Hook to fetch list of entitlements (admin endpoint)
 */
export function useAdminEntitlementsList() {
  return useQuery({
    queryKey: entitlementQueryKeys.all,
    queryFn: async () => {
      const response = await authenticatedApi.request<EntitlementListResponse>(
        '/v1/admin/entitlements'
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}
